export const OPEN_GRAPH_SITE_NAME = 'Blocktickets';
export const OPEN_GRAPH_DEFAULT_IMAGE = 'https://blocktickets.nyc3.cdn.digitaloceanspaces.com/logo.png';
const OPEN_GRAPH_CRAWLER = /OpenGraphXYZBot|opengraph\.xyz|facebookexternalhit|Facebot|Twitterbot|LinkedInBot|Discordbot|Slackbot|Slack-ImgProxy|WhatsApp|TelegramBot|Pinterest|redditbot|Quora-Link-Preview|vkShare|Embedly|Iframely|SkypeUriPreview|Bitlybot|Tumblr|Mastodon/i;
const ZONE_BY_ABBR = {
    EDT: 'America/New_York',
    EST: 'America/New_York',
    CDT: 'America/Chicago',
    CST: 'America/Chicago',
    MDT: 'America/Denver',
    MST: 'America/Denver',
    PDT: 'America/Los_Angeles',
    PST: 'America/Los_Angeles',
    AKDT: 'America/Anchorage',
    AKST: 'America/Anchorage',
    HST: 'Pacific/Honolulu',
};
const FALLBACK_COPY = {
    'flex-pack': {
        title: 'Flex Pack | Blocktickets',
        description: 'Buy a flex pack from Blocktickets.',
    },
    package: {
        title: 'Season Tickets | Blocktickets',
        description: 'Buy season tickets on Blocktickets.',
    },
    venue: {
        title: 'Event Calendar | Blocktickets',
        description: 'Buy tickets and find event information for upcoming events.',
    },
    event: {
        title: 'Event Tickets | Blocktickets',
        description: 'Buy event tickets on Blocktickets.',
    },
    default: {
        title: 'Blocktickets',
        description: 'Buy tickets, season passes, and flex packs on Blocktickets.',
    },
};
export function isOpenGraphCrawler(userAgent) {
    return OPEN_GRAPH_CRAWLER.test(String(userAgent || ''));
}
export function matchOpenGraphRoute(input) {
    const path = normalizePath(input);
    if (!path)
        return null;
    const eventTickets = path.match(/^\/e\/([^/]+)\/([^/]+)\/tickets$/);
    if (eventTickets) {
        return {
            kind: 'event',
            slug: decodeSegment(eventTickets[1]),
            shortcode: decodeSegment(eventTickets[2]),
        };
    }
    const event = path.match(/^\/e\/([^/]+)\/([^/]+)$/);
    if (event) {
        return {
            kind: 'event',
            slug: decodeSegment(event[1]),
            shortcode: decodeSegment(event[2]),
        };
    }
    const venuePackage = path.match(/^\/venue\/([^/]+)\/package\/([^/]+)$/);
    if (venuePackage) {
        return { kind: 'package', id: decodeSegment(venuePackage[2]) };
    }
    const venueFlexPack = path.match(/^\/venue\/([^/]+)\/flex-pack\/([^/]+)$/);
    if (venueFlexPack) {
        return { kind: 'flex-pack', uuid: decodeSegment(venueFlexPack[2]) };
    }
    const venue = path.match(/^\/venue\/([^/]+)$/);
    if (venue)
        return { kind: 'venue', slug: decodeSegment(venue[1]) };
    const eventPackage = path.match(/^\/([^/]+)\/package\/([^/]+)$/);
    if (eventPackage) {
        return { kind: 'package', id: decodeSegment(eventPackage[2]) };
    }
    const flexPack = path.match(/^\/([^/]+)\/flex-pack\/([^/]+)$/);
    if (flexPack) {
        return { kind: 'flex-pack', uuid: decodeSegment(flexPack[2]) };
    }
    return null;
}
export function fallbackOpenGraph(kind = 'default') {
    const copy = FALLBACK_COPY[kind] || FALLBACK_COPY.default;
    return {
        title: copy.title,
        description: copy.description,
        image: OPEN_GRAPH_DEFAULT_IMAGE,
        siteName: OPEN_GRAPH_SITE_NAME,
    };
}
export function buildFlexPackOpenGraph(flexPack) {
    const name = clean(flexPack?.name);
    const organization = clean(flexPack?.organization?.name);
    if (!name && !flexPack)
        return fallbackOpenGraph('flex-pack');
    return {
        title: name ? `${name} | Blocktickets` : 'Flex Pack | Blocktickets',
        description: plainText(flexPack?.description) ||
            `Buy a flex pack from ${organization || 'Blocktickets'}.`,
        image: pickImage(flexPack?.image, flexPack?.organization?.branding?.logo, flexPack?.organization?.image),
        siteName: OPEN_GRAPH_SITE_NAME,
    };
}
export function buildPackageOpenGraph(eventPackage) {
    const name = clean(eventPackage?.name);
    if (!name && !eventPackage)
        return fallbackOpenGraph('package');
    return {
        title: name ? `${name} | Season Tickets` : 'Season Tickets | Blocktickets',
        description: plainText(eventPackage?.description) ||
            `Buy season tickets for ${name || 'this package'}.`,
        image: pickImage(eventPackage?.image, eventPackage?.venue?.image, eventPackage?.organization?.branding?.logo, eventPackage?.organization?.image),
        siteName: OPEN_GRAPH_SITE_NAME,
    };
}
export function buildVenueOpenGraph(venue) {
    const name = clean(venue?.name);
    if (!name && !venue)
        return fallbackOpenGraph('venue');
    return {
        title: name ? `${name} | Event Calendar` : 'Event Calendar | Blocktickets',
        description: plainText(venue?.description) ||
            (name
                ? `Calendar | Buy tickets and find event information for upcoming events by ${name}`
                : FALLBACK_COPY.venue.description),
        image: pickImage(venue?.image, venue?.banner),
        siteName: OPEN_GRAPH_SITE_NAME,
    };
}
export function buildEventOpenGraph(event) {
    const name = clean(event?.name);
    if (!name)
        return fallbackOpenGraph('event');
    const address = firstAddress(event?.venue?.address);
    const city = titleCase(address.city);
    const state = stateLabel(address.state);
    const schedule = formatEventSchedule(event, event?.venue?.timezone);
    const venueName = clean(event?.venue?.name);
    const place = [venueName, [city, state].filter(Boolean).join(', ')]
        .filter(Boolean)
        .join(' - ');
    const summary = plainText(event?.summary);
    return {
        title: eventTitle(name, city, schedule),
        description: summary ||
            [name + ' Tickets', schedule, place].filter(Boolean).join(', '),
        image: pickImage(event?.image, event?.organization?.branding?.logo, event?.organization?.image, event?.venue?.image),
        siteName: OPEN_GRAPH_SITE_NAME,
    };
}
export function resolvePublicOrigin(input) {
    const fallback = normalizeOrigin(input?.fallback || 'https://blocktickets.xyz');
    const host = String(input?.host || '')
        .split(',')[0]
        .trim();
    const hostname = host.split(':')[0].toLowerCase();
    const allowed = hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname === 'blocktickets.xyz' ||
        hostname.endsWith('.blocktickets.xyz') ||
        hostname === 'ondigitalocean.app' ||
        hostname.endsWith('.ondigitalocean.app');
    if (!allowed || !host)
        return fallback;
    const proto = String(input?.proto || '')
        .split(',')[0]
        .trim()
        .toLowerCase();
    return `${proto === 'http' ? 'http' : 'https'}://${host}`;
}
export function renderOpenGraphHead(meta) {
    const title = escapeHtml(meta.title);
    const description = escapeHtml(meta.description);
    const image = escapeHtml(meta.image || OPEN_GRAPH_DEFAULT_IMAGE);
    const url = escapeHtml(meta.url);
    const siteName = escapeHtml(meta.siteName || OPEN_GRAPH_SITE_NAME);
    return [
        `<title data-rh="true">${title}</title>`,
        `<meta data-rh="true" name="description" content="${description}" />`,
        `<meta data-rh="true" property="og:site_name" content="${siteName}" />`,
        `<meta data-rh="true" property="og:type" content="website" />`,
        `<meta data-rh="true" property="og:url" content="${url}" />`,
        `<meta data-rh="true" property="og:title" content="${title}" />`,
        `<meta data-rh="true" property="og:description" content="${description}" />`,
        `<meta data-rh="true" property="og:image" content="${image}" />`,
        `<meta data-rh="true" name="twitter:card" content="summary_large_image" />`,
        `<meta data-rh="true" name="twitter:title" content="${title}" />`,
        `<meta data-rh="true" name="twitter:description" content="${description}" />`,
        `<meta data-rh="true" name="twitter:image" content="${image}" />`,
    ].join('\n');
}
export function renderOpenGraphDocument(meta) {
    return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
${renderOpenGraphHead(meta)}
</head>
<body>
<p>${escapeHtml(meta.title)}</p>
<p>${escapeHtml(meta.description)}</p>
</body>
</html>`;
}
const OPEN_GRAPH_START = '<!-- blocktickets-open-graph -->';
const OPEN_GRAPH_END = '<!-- /blocktickets-open-graph -->';
export function injectOpenGraphHead(html, head) {
    const block = `${OPEN_GRAPH_START}\n${head}\n${OPEN_GRAPH_END}`;
    const existing = new RegExp(`${escapeRegExp(OPEN_GRAPH_START)}[\\s\\S]*?${escapeRegExp(OPEN_GRAPH_END)}`);
    if (existing.test(html))
        return html.replace(existing, block);
    if (html.includes('</head>'))
        return html.replace('</head>', `${block}\n</head>`);
    return `${block}\n${html}`;
}
export function plainText(value, maxLength = 200) {
    const text = String(value ?? '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;|&apos;/gi, "'")
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/\s+/g, ' ')
        .trim();
    if (text.length <= maxLength)
        return text;
    return `${text.slice(0, maxLength - 1).trimEnd()}…`;
}
function eventTitle(name, city, schedule) {
    let title = `Buy Ticket to ${name}`;
    if (city)
        title += ` in ${city}`;
    if (schedule)
        title += ` on ${schedule}`;
    return title;
}
function formatEventSchedule(event, timezone) {
    if (!event)
        return '';
    if (event.date_tbd || event.start_tbd)
        return 'Date & Time TBD';
    if (!event.start)
        return '';
    const date = new Date(event.start);
    if (Number.isNaN(date.getTime()))
        return '';
    const zone = zoneFor(timezone);
    const dateLabel = formatPart(date, zone, { month: 'short', day: 'numeric' });
    if (!dateLabel)
        return '';
    if (event.time_tbd || event.display_start_time === false)
        return dateLabel;
    const timeLabel = formatPart(date, zone, {
        hour: 'numeric',
        minute: '2-digit',
    });
    return timeLabel ? `${dateLabel} · ${timeLabel}` : dateLabel;
}
function formatPart(date, zone, options) {
    try {
        return new Intl.DateTimeFormat('en-US', { ...options, timeZone: zone })
            .format(date)
            .replace(/\u202f/g, ' ')
            .replace(/\u00a0/g, ' ');
    }
    catch {
        return '';
    }
}
function zoneFor(timezone) {
    const raw = String(timezone || '').trim();
    if (raw.includes('/')) {
        try {
            Intl.DateTimeFormat('en-US', { timeZone: raw }).format(new Date());
            return raw;
        }
        catch {
            return 'America/New_York';
        }
    }
    return ZONE_BY_ABBR[raw.toUpperCase()] || 'America/New_York';
}
function firstAddress(address) {
    const entry = Array.isArray(address) ? address[0] : address;
    if (!entry || typeof entry !== 'object')
        return {};
    return entry;
}
function stateLabel(value) {
    const raw = String(value || '').trim();
    if (/^[a-z]{2}$/i.test(raw))
        return raw.toUpperCase();
    return titleCase(raw);
}
function titleCase(value) {
    return String(value || '')
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}
function pickImage(...images) {
    for (const image of images) {
        const url = mediaUrl(image);
        if (url)
            return url;
    }
    return OPEN_GRAPH_DEFAULT_IMAGE;
}
function mediaUrl(image) {
    if (Array.isArray(image)) {
        for (const entry of image) {
            const url = mediaUrl(entry);
            if (url)
                return url;
        }
        return '';
    }
    if (!image)
        return '';
    if (typeof image === 'string')
        return cleanUrl(image);
    return cleanUrl(image.url);
}
function cleanUrl(url) {
    const value = String(url || '').trim();
    if (!value || value === 'undefined' || value === 'null')
        return '';
    return value;
}
function clean(value) {
    return String(value || '').trim();
}
function normalizePath(input) {
    const withoutHash = String(input || '')
        .trim()
        .split('#')[0]
        .split('?')[0];
    if (!withoutHash)
        return '';
    const withSlash = withoutHash.startsWith('/')
        ? withoutHash
        : `/${withoutHash}`;
    return withSlash.replace(/\/+$/, '') || '/';
}
function decodeSegment(value) {
    try {
        return decodeURIComponent(value);
    }
    catch {
        return value;
    }
}
function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}
function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
function normalizeOrigin(value) {
    const trimmed = String(value || '')
        .trim()
        .replace(/\/$/, '');
    if (!trimmed)
        return 'https://blocktickets.xyz';
    if (/^https?:\/\//i.test(trimmed))
        return trimmed;
    return `https://${trimmed}`;
}
